import { Model } from 'app/model/backendmodel';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Metamodel } from 'app/model/base/model';
import { ActivityAsyncAdapter } from 'app/model/adapters/activities/activity.adapter';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { FilterActivity } from 'app/model/pipeline/filter.activity';
import { IFilterActivityContext } from 'app/interfaces/copilot.pipeline.action.types';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(FilterActivity),
    asynchronous: true,
    adapts: {
        name: '*',
        description: '*',
        type: '*',
        dependsOn: '*',
        userProperties: '*',
        typeProperties: {
            items: '*',
            condition: '*'
        }
    }
})
export class FilterActivityAdapter extends ActivityAsyncAdapter<FilterActivity, Model.FilterActivity, IFilterActivityContext> {
    public override async toFabricCopilotContext(activity: FilterActivity): Promise<IFilterActivityContext> {
        if (activity) {
            const activityContext: IFilterActivityContext = {
                ...(await super.toFabricCopilotContext(activity)),
                ItemsExpression: FilterActivity.ITEMS.templateValue(activity),
                ConditionExpression: FilterActivity.CONDITION.templateValue(activity)
            };
            return activityContext;
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(
        context: IFilterActivityContext,
        model?: FilterActivity
    ): Promise<FilterActivity> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            if (context.ItemsExpression != null) {
                FilterActivity.ITEMS.setTemplateValue(model, context.ItemsExpression);
            }
            if (context.ConditionExpression != null) {
                FilterActivity.CONDITION.setTemplateValue(model, context.ConditionExpression);
            }
        }
        return model;
    }

    protected async _toResource(activity: FilterActivity): Promise<Model.FilterActivity> {
        const modelFilterActivity: Model.FilterActivity = super._getCommonActivityProperties(activity, 'Filter') as Model.FilterActivity;
        modelFilterActivity.typeProperties = modelFilterActivity.typeProperties || { items: undefined, condition: undefined};
        if (activity.items && !FilterActivity.ITEMS.templateValue(activity)) {
            FilterActivity.ITEMS.setTemplateValue(activity, activity.items);
            activity.items = undefined;
        }
        if (activity.condition && !FilterActivity.CONDITION.templateValue(activity)) {
            FilterActivity.CONDITION.setTemplateValue(activity, activity.condition);
            activity.condition = undefined;
        }
        modelFilterActivity.typeProperties.items = ValueAdapter.toResource(activity, FilterActivity.ITEMS);
        modelFilterActivity.typeProperties.condition = ValueAdapter.toResource(activity, FilterActivity.CONDITION);
        if (activity.raw) {
            const parsed = JSON.parse(activity.raw);
            _.merge(modelFilterActivity, parsed);
        }

        return modelFilterActivity;
    }

    protected async _toModel(modelActivity: Model.FilterActivity): Promise<FilterActivity> {
        const activity: FilterActivity = FilterActivity.MODEL.create<FilterActivity>(modelActivity.name);
        this._updatedCommonProperties(modelActivity, activity);
        modelActivity.typeProperties = modelActivity.typeProperties || { items: undefined, condition: undefined };

        ValueAdapter.toModel(activity, FilterActivity.ITEMS, modelActivity.typeProperties.items);
        ValueAdapter.toModel(activity, FilterActivity.CONDITION, modelActivity.typeProperties.condition);

        activity.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelActivity, FilterActivity.MODEL));
        return activity;
    }
}
